// The spectator's screen: the board, read-only. No panels that issue orders,
// no chat, no tick submitter — a spectator is not a seat. It builds the same
// world as the players from the match header and replays the same frames.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { FrameSync } from '../multiplayer/match/frameSync.js';
import { useMpSimWorld } from '../multiplayer/match/useMpSimWorld.js';
import { Scene } from '../render/Scene.js';
import { type SpectateFeed, headerToMatchInfo } from './spectateFeed.js';
import type { SpectateHeader } from './spectateFeed.js';
import { useSpectateSession } from './useSpectateSession.js';

const FRAMES_PER_SLICE = 40;
const noop = () => {};

interface BoardProps {
  header: SpectateHeader;
  serverUrl: string;
  feed: SpectateFeed;
  seat: number;
  onLost: () => void;
  onTick: (tick: number) => void;
}

/** One board for one point of view. Re-keyed per seat, so it replays from frame 0. */
function SpectatorBoard({ header, serverUrl, feed, seat, onLost, onTick }: BoardProps) {
  const sim = useMpSimWorld(headerToMatchInfo(header, serverUrl), seat);
  const ready = sim.status === 'ready';
  const simRef = useRef(sim);
  simRef.current = sim;

  useEffect(() => {
    if (!ready) return;
    const sync = new FrameSync({
      applyFrame: (f) => simRef.current.applyFrame(f),
      step: () => simRef.current.externalStep(1),
      getHash: () => simRef.current.getDeterminismHash().toString(),
      // A hash stamped on a delayed feed still has to agree; if not, the
      // viewer's world is wrong and so would be everything after it.
      onMismatch: () => onLost(),
    });
    // A long match arrives as one big backlog: apply it in slices so the page
    // stays alive while the viewer catches up.
    const queue: Parameters<typeof sync.receive>[0][] = [];
    let timer: ReturnType<typeof setTimeout> | null = null;
    const drain = () => {
      timer = null;
      for (let i = 0; i < FRAMES_PER_SLICE && queue.length > 0; i++) {
        const f = queue.shift();
        if (!f) break;
        if (sync.receive(f) === 'gap') {
          onLost();
          queue.length = 0;
          return;
        }
        onTick(f.tick);
      }
      if (queue.length > 0) timer = setTimeout(drain, 0);
    };
    const detach = feed.attach((f) => {
      queue.push(f);
      if (timer === null) timer = setTimeout(drain, 0);
    });
    return () => {
      detach();
      if (timer !== null) clearTimeout(timer);
    };
  }, [ready, feed, onLost, onTick]);

  if (sim.status === 'error') return <div className="p-8 text-red-400">Failed to load sim</div>;
  if (sim.snapshot === null) return null;
  return (
    <Scene
      snapshot={sim.snapshot}
      onTileHover={noop}
      onTileClick={noop}
      onCityClick={noop}
      onUnitClick={noop}
      onMoveClick={noop}
      onBackgroundClick={noop}
      selectedTileId={null}
      selectedCityId={null}
      selectedUnitId={null}
    />
  );
}

const REASONS: Record<string, string> = {
  'signed-out': 'Sign in to watch a match.',
  'no-such-match': 'That match is not running here (or the link is wrong).',
  unreachable: 'Could not reach the match. Try again in a moment.',
  rejected: 'The match refused the pass.',
};

export function SpectatorView({
  matchId,
  onBack,
}: {
  matchId: string;
  onBack: () => void;
}) {
  const session = useSpectateSession(matchId);
  const [seat, setSeat] = useState(0);
  const [lost, setLost] = useState(false);
  const [tick, setTick] = useState(0);
  const onLost = useCallback(() => setLost(true), []);
  const onTick = useCallback((t: number) => setTick(t), []);

  const seats = session.header?.seatCount ?? 0;
  let body: React.ReactNode = null;
  if (session.phase === 'error') {
    body = (
      <Centered>
        <p data-testid="spectate-error">{REASONS[session.error ?? 'unreachable']}</p>
      </Centered>
    );
  } else if (session.phase !== 'watching' || !session.header) {
    body = (
      <Centered>
        <p>Joining the match…</p>
      </Centered>
    );
  } else {
    body = (
      <SpectatorBoard
        key={seat}
        header={session.header}
        serverUrl={session.serverUrl}
        feed={session.feed}
        seat={seat}
        onLost={onLost}
        onTick={onTick}
      />
    );
  }

  return (
    <ErrorBoundary>
      <div className="relative h-full w-full" data-testid="spectator-view">
        {body}
        <div className="absolute left-0 right-0 top-0 z-50 flex items-center gap-3 bg-panel-scrim px-4 py-2 text-sm text-foreground backdrop-blur">
          <span className="font-semibold">Watching</span>
          <span className="text-muted-foreground" data-testid="spectate-tick">
            tick {tick}
          </span>
          {seats > 1 && (
            <label className="flex items-center gap-1 text-xs">
              View as
              <select
                className="rounded border border-border bg-transparent px-1 py-0.5"
                value={seat}
                data-testid="spectate-seat"
                onChange={(e) => {
                  setTick(0);
                  setLost(false);
                  setSeat(Number(e.target.value));
                }}
              >
                {Array.from({ length: seats }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: seats are positional
                  <option key={i} value={i}>
                    Player {i + 1}
                  </option>
                ))}
              </select>
            </label>
          )}
          <span className="flex-1" />
          <button
            type="button"
            className="rounded bg-sky-600 px-3 py-1 font-bold text-white hover:bg-sky-500"
            onClick={onBack}
            data-testid="spectate-back"
          >
            Leave
          </button>
        </div>
        {session.ended && (
          <output
            className="absolute left-1/2 top-14 z-50 -translate-x-1/2 rounded bg-emerald-500/15 px-3 py-1.5 text-xs text-emerald-800 backdrop-blur dark:text-emerald-200"
            data-testid="spectate-ended"
          >
            The match is over.
          </output>
        )}
        {lost && (
          <output
            className="absolute left-1/2 top-14 z-50 -translate-x-1/2 rounded bg-amber-500/15 px-3 py-1.5 text-xs text-amber-700 backdrop-blur dark:text-yellow-200"
            data-testid="spectate-lost"
          >
            This view fell out of step with the match. Leave and watch again.
          </output>
        )}
      </div>
    </ErrorBoundary>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full items-center justify-center text-foreground">{children}</div>
  );
}
